import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Eye, Trash2, Plus, Loader2, ShieldAlert } from 'lucide-react';

export const CuitsVigilados = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [texto, setTexto] = useState('');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/waya-movimientos/cuits-vigilados');
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('No se pudo cargar la lista de CUITs vigilados');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const agregar = async () => {
    const cuits = texto.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (!cuits.length) { toast.error('Pegá al menos un CUIT'); return; }
    setSaving(true);
    try {
      const r = await api.post('/waya-movimientos/cuits-vigilados', { cuits, nota });
      const d = r.data || {};
      const extra = [];
      if (d.duplicados) extra.push(`ya estaban: ${d.duplicados}`);
      if (d.invalidos?.length) extra.push(`inválidos: ${d.invalidos.length}`);
      toast.success(`Agregados: ${d.agregados || 0}${extra.length ? ` · ${extra.join(' · ')}` : ''}`);
      setTexto(''); setNota('');
      cargar();
    } catch (e) {
      toast.error('No se pudieron agregar los CUITs');
    } finally { setSaving(false); }
  };

  const quitar = async (cuit) => {
    try {
      await api.delete(`/waya-movimientos/cuits-vigilados/${cuit}`);
      setItems(prev => prev.filter(i => i.cuit !== cuit));
      toast.success('CUIT quitado de la vigilancia');
    } catch (e) { toast.error('No se pudo quitar el CUIT'); }
  };

  return (
    <div data-testid="waya-vigilados-section" className="space-y-4">
      <Card className="p-4 bg-white border-l-4 border-l-red-500">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={18} className="text-red-600" />
          <div>
            <h3 className="text-sm font-semibold text-slate-800">CUITs vigilados</h3>
            <p className="text-xs text-slate-500">
              Si un CUIT de esta lista aparece como origen o destino de una transferencia,
              se genera una alerta <strong className="text-red-600">ALTA</strong> en la bandeja.
              Aplica a las extracciones que se procesen de ahora en más.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-slate-600">CUIT(s) — pegá uno o varios (separados por espacio, coma o salto de línea)</Label>
            <textarea
              data-testid="vigilados-textarea"
              value={texto}
              onChange={e => setTexto(e.target.value)}
              rows={3}
              placeholder="20-12345678-9  27-98765432-1 ..."
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-200"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <Label className="text-xs text-slate-600">Nota / motivo (opcional)</Label>
              <Input
                data-testid="vigilados-nota"
                value={nota}
                onChange={e => setNota(e.target.value)}
                placeholder="Ej: solicitado por UIF / reportado / a revisar"
                className="mt-1 text-sm"
              />
            </div>
            <Button
              onClick={agregar}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="vigilados-agregar-btn"
            >
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
              Agregar a la vigilancia
            </Button>
          </div>
        </div>
      </Card>

      <Card className="bg-white overflow-hidden" data-testid="vigilados-tabla">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
            <Eye size={14} /> Lista de vigilancia ({items.length})
          </span>
          <Button variant="outline" size="sm" onClick={cargar} disabled={loading} data-testid="vigilados-refresh">
            {loading ? <Loader2 size={13} className="animate-spin" /> : 'Actualizar'}
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="vigilados-empty">
            No hay CUITs en la lista de vigilancia. Agregá alguno arriba.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[55vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">CUIT</th>
                  <th className="px-3 py-2 text-left font-semibold">Nombre (padrón)</th>
                  <th className="px-3 py-2 text-left font-semibold">Nota</th>
                  <th className="px-3 py-2 text-left font-semibold">Agregado</th>
                  <th className="px-3 py-2 text-right font-semibold">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={it.cuit} className="hover:bg-slate-50" data-testid={`vigilados-row-${idx}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{it.cuit_fmt || it.cuit}</td>
                    <td className="px-3 py-2 text-slate-700">{it.nombre || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-slate-600">{it.nota || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                      {(it.created_at || '').slice(0, 10)}
                      {it.created_by ? <span className="block text-[10px] text-slate-400">{it.created_by}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => quitar(it.cuit)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                        data-testid={`vigilados-quitar-${idx}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CuitsVigilados;
